/**
 * PutProjectOnHoldModal.jsx — "Project hold…" from a project's ⋯ menu (2026-08). See
 * docs/PROJECT_HOLD.md. Fetches the project's current hold status on open and branches its own UI
 * between activating a new hold and resuming an existing one — the ⋯ menu item itself is a single
 * static label ("Project hold…") since the legacy ES5 outline menu can't conditionally render two
 * different labels without threading Supabase state into it.
 */

import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { ctx } from '../outline/ctx.js'
import { getProjectHoldStatus, activateProjectHold, resumeProjectHold } from '../services/projectHold.js'
import { confirmDialog } from './Dialog.jsx'

export default function PutProjectOnHoldModal() {
  const [open, setOpen] = useState(false)
  const [projId, setProjId] = useState(null)
  const [loading, setLoading] = useState(false)
  const [hold, setHold] = useState(null)       // active hold row, or null
  const [reason, setReason] = useState('')
  const [milestoneName, setMilestoneName] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    function onOpen(e) {
      const id = e.detail && e.detail.projId
      setProjId(id)
      setReason(''); setMilestoneName(''); setError(''); setSaving(false)
      setHold(null); setLoading(true)
      setOpen(true)
      getProjectHoldStatus(id).then(r => setHold(r && r.hold ? r.hold : null)).finally(() => setLoading(false))
    }
    window.addEventListener('open-project-hold-modal', onOpen)
    return () => window.removeEventListener('open-project-hold-modal', onOpen)
  }, [])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && open) close() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  function close() { setOpen(false); setProjId(null) }

  const proj = projId ? (ctx.projectsCache || []).find(p => p.id === projId) : null
  const projName = proj ? (proj.fields['Project Name'] || 'This project') : 'This project'

  async function handleActivate() {
    if (saving) return
    setSaving(true); setError('')
    try {
      const r = await activateProjectHold(projId, { reason: reason.trim() || null, blockingMilestoneName: milestoneName.trim() || null })
      if (r.error) throw new Error(r.error)
      if (ctx.projectHoldIds) ctx.projectHoldIds.add(projId)
      window.dispatchEvent(new CustomEvent('crew-index-changed'))
      close()
      window.dispatchEvent(new CustomEvent('outline-render'))
    } catch (e) {
      setSaving(false)
      setError(e.message || 'Could not put this project on hold — please try again.')
    }
  }

  async function handleResume() {
    if (saving) return
    const ok = await confirmDialog({
      title: 'Resume project?',
      message: `${projName} will go back to active — billing, progress updates, and scheduling all resume as normal.`,
      confirmLabel: 'Resume project',
    })
    if (!ok) return
    setSaving(true); setError('')
    try {
      const r = await resumeProjectHold(projId)
      if (r.error) throw new Error(r.error)
      if (ctx.projectHoldIds) ctx.projectHoldIds.delete(projId)
      close()
      window.dispatchEvent(new CustomEvent('outline-render'))
    } catch (e) {
      setSaving(false)
      setError(e.message || 'Could not resume this project — please try again.')
    }
  }

  if (!open) return null

  const label = { fontFamily: '"DM Sans",sans-serif', fontSize: 11, fontWeight: 500, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', marginBottom: 6 }
  const input = { width: '100%', fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)', background: 'var(--aj-bg-page)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '8px 10px', boxSizing: 'border-box' }
  const btnBase = { border: 'none', borderRadius: 4, padding: '8px 18px', fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 500, cursor: 'pointer' }

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div data-modal-id="PutProjectOnHoldModal" style={{
        background: 'var(--aj-bg-elevated)', borderRadius: 10, border: '0.5px solid var(--aj-border)',
        padding: '22px 24px', width: 420, maxWidth: '100%', boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
      }}>
        <div style={{ fontFamily: '"DM Sans",sans-serif', fontWeight: 500, fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text)', marginBottom: 16 }}>
          Project hold
        </div>
        <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)', padding: '8px 10px', background: 'var(--aj-bg-subtle)', borderRadius: 4, fontWeight: 500, marginBottom: 16 }}>
          {projName}
        </div>

        {loading ? (
          <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text-muted)', padding: '8px 0' }}>Checking status…</div>
        ) : hold ? (
          <>
            <p style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, lineHeight: 1.5, color: 'var(--aj-text)', margin: '0 0 12px' }}>
              This project has been on hold since {new Date(hold.held_at).toLocaleDateString()}
              {hold.blocking_milestone_name ? <> — waiting on <strong>{hold.blocking_milestone_name}</strong></> : null}.
              Billing, progress updates, and scheduling are all paused. Every 30 days the team gets a
              Slack reminder and the Client Manager is asked to check in with the client personally.
            </p>
            {hold.reason && (
              <div style={{ ...input, background: 'var(--aj-bg-subtle)', marginBottom: 12, whiteSpace: 'pre-wrap' }}>{hold.reason}</div>
            )}
          </>
        ) : (
          <>
            <p style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, lineHeight: 1.5, color: 'var(--aj-text)', margin: '0 0 14px' }}>
              Puts this project's Contract Stage on hold — billing, progress updates, and scheduling
              all stop until you resume it. Its planes come down: their crews are released and the tower
              crews them again on resume. The client is not emailed. The team gets a Slack post now
              and every 30 days, and the Client Manager is reminded every 30 days to check in with
              the client personally.
            </p>
            <div style={label}>Waiting on (optional)</div>
            <input
              style={{ ...input, marginBottom: 12 }} placeholder="e.g. Client review of DD set"
              value={milestoneName} onChange={e => setMilestoneName(e.target.value)} disabled={saving}
            />
            <div style={label}>Internal note (optional — Slack only, never shown to the client)</div>
            <textarea
              style={{ ...input, minHeight: 64, resize: 'vertical', fontFamily: '"DM Sans",sans-serif' }}
              value={reason} onChange={e => setReason(e.target.value)} disabled={saving}
            />
          </>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', marginTop: 20 }}>
          {error && <span style={{ fontSize: 12, color: '#c0392b', marginRight: 'auto' }}>{error}</span>}
          <button onClick={close} style={{ ...btnBase, background: 'transparent', border: '0.5px solid var(--aj-border-mid)', color: 'var(--aj-text-mid)' }}>
            {hold ? 'Close' : 'Cancel'}
          </button>
          {!loading && (hold ? (
            <button onClick={handleResume} disabled={saving} style={{ ...btnBase, background: '#07272D', color: 'white', opacity: saving ? 0.5 : 1 }}>
              {saving ? 'Resuming…' : 'Resume project'}
            </button>
          ) : (
            <button onClick={handleActivate} disabled={saving} style={{ ...btnBase, background: '#c0392b', color: 'white', opacity: saving ? 0.5 : 1 }}>
              {saving ? 'Putting on hold…' : 'Put on hold'}
            </button>
          ))}
        </div>
      </div>
    </div>,
    document.body
  )
}
